import {
  type ComponentType,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { FaTrophy } from "react-icons/fa";
import Spinner from "../../../shared/components/Spinner";
import UserAvatar from "../../../shared/components/UserAvatar";
import useBodyScrollLock from "../../../shared/hooks/useBodyScrollLock";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import {
  getSiteLanguage,
  useSiteLanguage,
} from "../../../shared/i18n/language";
import { formatDateTimeLabel } from "../../../shared/utils/dateTime";
import {
  getPublicTournamentBracket,
  getPublicTournamentSchedule,
  type PublicTournamentBracket,
  type PublicTournamentBracketMatch,
  type PublicTournamentSchedule,
  type PublicTournamentScheduleSlot,
} from "../services/challengeAdminService";
import "./PublicTournamentProgressPanel.css";

type TournamentCategory = {
  categoryKey: string;
  categoryName: string;
};

type PublicTournamentProgressPanelProps = {
  tournamentId: number;
  token?: string;
  categories: TournamentCategory[];
  mode: "brackets" | "timeline";
  eventTitle?: string | null;
  eventDate?: string | null;
  eventLocation?: string | null;
  selectedCategoryKey?: string;
  onSelectedCategoryKeyChange?: (categoryKey: string) => void;
  showInternalCategoryTabs?: boolean;
};

type PublicBracketBlock = {
  id: string;
  title: string;
  subtitle: string;
  kind: "group" | "round";
  isSeedHead?: boolean;
  teams: Array<{
    registrationId: number;
    label: string;
    avatarUrls: Array<string | null | undefined>;
    sexes: Array<string | null | undefined>;
    profileHrefs: Array<string | undefined>;
    isSeedHead: boolean;
    seedRank?: number | null;
  }>;
  matches: PublicTournamentBracketMatch[];
};

type PublicTournamentChampionSummary = {
  championLabel: string;
  runnersUpLabel: string;
  categoryName: string;
  finalScore: string;
  championAvatarUrls: Array<string | null | undefined>;
  runnersUpAvatarUrls: Array<string | null | undefined>;
  championSexes: Array<string | null | undefined>;
  runnersUpSexes: Array<string | null | undefined>;
  championProfileHrefs: Array<string | undefined>;
};

type ResultShareLabels = {
  title: string;
  championBadge: string;
  championSideTitle: string;
  runnerTitle: string;
};

type ResultShareGender = "female" | "male" | "mixed" | "neutral";

type SharePreviewState = {
  blob: Blob;
  filename: string;
  url: string;
} | null;

type IconLike = ComponentType<Record<string, unknown>>;

const TrophyIcon = FaTrophy as unknown as IconLike;
const progressTexts = siteTexts.challenges.publicDetail.tournamentProgress;
const LOCALE_BY_LANGUAGE = { pt: "pt-BR", en: "en-US", es: "es-ES" } as const;

function normalizeAvatarSex(
  value?: string | null,
): "male" | "female" | "other" | "" {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "male" || normalized === "masculino") return "male";
  if (normalized === "female" || normalized === "feminino") return "female";
  if (normalized === "other" || normalized === "outro") return "other";
  return "";
}

function currentDateLocale() {
  const language = getSiteLanguage();
  return language === "pt" ? "pt-BR" : language;
}

function inferResultShareGender(
  categoryName?: string | null,
): ResultShareGender {
  const normalized = String(categoryName || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");
  if (
    /\bfem\b/.test(normalized) ||
    normalized.includes("feminina") ||
    normalized.includes(" feminino") ||
    normalized.includes("femenina") ||
    normalized.includes(" femenino")
  ) {
    return "female";
  }
  if (
    /\bmasc\b/.test(normalized) ||
    normalized.includes("masculina") ||
    normalized.includes(" masculino")
  ) {
    return "male";
  }
  if (
    normalized.includes("mista") ||
    normalized.includes("misto") ||
    normalized.includes("mixta") ||
    normalized.includes("mixto") ||
    normalized.includes("mixed")
  ) {
    return "mixed";
  }
  return "neutral";
}

function getResultShareLabels(categoryName?: string | null): ResultShareLabels {
  const gender = inferResultShareGender(categoryName);
  const labels = siteTexts.challenges.publicTournamentProgress.canvas.championLabels;
  if (gender === "female") return labels.female;
  if (gender === "male") return labels.male;
  if (gender === "mixed") return labels.mixed;
  return labels.neutral;
}

function publicPlayerHref(playerId?: number | null) {
  const safeId = Number(playerId || 0);
  return Number.isFinite(safeId) && safeId > 0
    ? `#/jogadores/${safeId}`
    : undefined;
}

function avatarItemsForLabel({
  avatarSexes,
  avatarUrls,
  label,
  showAvatars = false,
}: {
  avatarSexes: Array<string | null | undefined>;
  avatarUrls: Array<string | null | undefined>;
  label: string;
  showAvatars?: boolean;
}) {
  const safeAvatarUrls = avatarUrls
    .map((value) => String(value || "").trim())
    .slice(0, 2);
  const playerNames = String(label || "")
    .split(" / ")
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 2);
  const avatarItems = playerNames.map((name, index) => ({
    avatarUrl: safeAvatarUrls[index] || "",
    name,
    sex: normalizeAvatarSex(avatarSexes[index]),
  }));
  const shouldShowAvatars = showAvatars || safeAvatarUrls.some(Boolean);

  return shouldShowAvatars ? avatarItems : [];
}

function PublicTournamentSideAvatars({
  avatarSize = 42,
  avatarSexes,
  avatarUrls,
  label,
  showAvatars = false,
}: {
  avatarSize?: number;
  avatarSexes: Array<string | null | undefined>;
  avatarUrls: Array<string | null | undefined>;
  label: string;
  showAvatars?: boolean;
}) {
  const avatarItems = avatarItemsForLabel({
    avatarSexes,
    avatarUrls,
    label,
    showAvatars,
  });
  if (avatarItems.length === 0) return null;
  return (
    <span className="public-tournament-match-side-avatars" aria-hidden="true">
      {avatarItems.map((avatar, index) => (
        <UserAvatar
          key={`${label}-${index}`}
          className="public-tournament-match-side-avatar"
          name={avatar.name}
          sex={avatar.sex}
          avatarUrl={avatar.avatarUrl}
          size={avatarSize}
        />
      ))}
    </span>
  );
}

function PublicTournamentMatchSide({
  avatarSize = 42,
  avatarSexes,
  avatarUrls,
  badge,
  className = "",
  label,
  profileHrefs = [],
  showAvatars = false,
}: {
  avatarSize?: number;
  avatarSexes: Array<string | null | undefined>;
  avatarUrls: Array<string | null | undefined>;
  badge?: string;
  className?: string;
  label: string;
  profileHrefs?: Array<string | undefined>;
  showAvatars?: boolean;
}) {
  const playerNames = String(label || "")
    .split(" / ")
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 2);
  const avatarItems = avatarItemsForLabel({
    avatarSexes,
    avatarUrls,
    label,
    showAvatars,
  });
  const showPairIdentity = playerNames.length === 2 && avatarItems.length === 2;
  const renderName = (name: string, index: number) => {
    const profileHref = profileHrefs[index];
    if (!profileHref) return name;
    return (
      <a
        className="public-tournament-player-profile-link"
        href={profileHref}
        onClick={(event) => event.stopPropagation()}
      >
        {name}
      </a>
    );
  };
  const singleProfileHref = profileHrefs.filter(Boolean)[0];

  return (
    <span
      className={`public-tournament-match-side${
        showPairIdentity ? " is-pair" : ""
      } ${className}`.trim()}
    >
      {showPairIdentity ? (
        avatarItems.map((avatar, index) => (
          <span
            key={`${label}-member-${index}`}
            className="public-tournament-pair-member"
          >
            <UserAvatar
              className="public-tournament-match-side-avatar"
              name={avatar.name}
              sex={avatar.sex}
              avatarUrl={avatar.avatarUrl}
              size={avatarSize}
            />
            <span className="public-tournament-pair-player-name">
              {renderName(avatar.name, index)}
            </span>
          </span>
        ))
      ) : (
        <>
          <PublicTournamentSideAvatars
            avatarSize={avatarSize}
            avatarSexes={avatarSexes}
            avatarUrls={avatarUrls}
            label={label}
            showAvatars={showAvatars}
          />
          <span>
            {singleProfileHref ? (
              <a
                className="public-tournament-player-profile-link"
                href={singleProfileHref}
                onClick={(event) => event.stopPropagation()}
              >
                {label}
              </a>
            ) : (
              label
            )}
          </span>
        </>
      )}
      {badge && (
        <small className="public-tournament-seed-badge">{badge}</small>
      )}
    </span>
  );
}

function formatFinalScoreLabel(
  match: PublicTournamentBracketMatch,
  _bracket: PublicTournamentBracket | null | undefined,
) {
  const sets = getVisibleScoreSets(match);
  if (sets.length === 0 || !hasUsableScoreSets(sets)) return "";
  return sets.map((set) => `${set.gamesA}x${set.gamesB}`).join(siteTexts.challenges.publicTournamentProgress.scoreSeparator);
}

function matchStatusLabel(status?: string | null) {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  if (normalized === "finished") return progressTexts.statuses.finished;
  if (normalized === "in_progress") return progressTexts.statuses.inProgress;
  if (normalized === "scheduled") return progressTexts.statuses.scheduled;
  return progressTexts.statuses.pending;
}

function matchStatusKey(status?: string | null) {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  if (normalized === "finished") return "finished";
  if (normalized === "in_progress") return "in-progress";
  if (normalized === "scheduled") return "scheduled";
  return "waiting";
}

function timelineSlotStatus(slot: {
  slotStartAt?: string | null;
  status?: string | null;
  matchStatus?: string | null;
}) {
  const matchStatus = String(slot.matchStatus || "")
    .trim()
    .toLowerCase();
  if (matchStatus && matchStatus !== "pending") return matchStatus;
  const slotStatus = String(slot.status || matchStatus || "pending")
    .trim()
    .toLowerCase();
  if (slotStatus === "pending" && String(slot.slotStartAt || "").trim()) {
    return "scheduled";
  }
  return slotStatus;
}

function bracketMatchStatus(
  match: PublicTournamentBracketMatch,
  slot?: NonNullable<PublicTournamentSchedule["slots"]>[number],
) {
  return slot
    ? timelineSlotStatus(slot)
    : String(match.status || "pending")
        .trim()
        .toLowerCase();
}

function isFinalMatch(match?: PublicTournamentBracketMatch | null) {
  const roundCode = String(match?.roundCode || "")
    .trim()
    .toLowerCase();
  return roundCode === "final" || roundCode === "round_of_2";
}

function scoreNumberFromKeys(
  value: PublicTournamentMatchSetLike,
  keys: Array<keyof PublicTournamentMatchSetLike>,
) {
  for (const key of keys) {
    const rawValue = value[key];
    if (rawValue !== null && rawValue !== undefined && rawValue !== "") {
      const parsed = Number(rawValue);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

type PublicTournamentMatchSetLike =
  PublicTournamentBracketMatch["sets"][number] & {
    games_a?: number | string | null;
    games_b?: number | string | null;
    tie_break_a?: number | string | null;
    tie_break_b?: number | string | null;
  };

function getVisibleScoreSets(match: PublicTournamentBracketMatch) {
  if (!Array.isArray(match.sets) || match.sets.length === 0) return [];
  const hasRegularSets = match.sets.some((set) => !set.isMatchTieBreak);
  return match.sets
    .filter((set) => !set.isMatchTieBreak || !hasRegularSets)
    .map((set, index) => {
      const setScore = set as PublicTournamentMatchSetLike;
      const gamesA = scoreNumberFromKeys(setScore, ["gamesA", "games_a"]);
      const gamesB = scoreNumberFromKeys(setScore, ["gamesB", "games_b"]);
      const tieBreakA = scoreNumberFromKeys(setScore, [
        "tieBreakA",
        "tie_break_a",
      ]);
      const tieBreakB = scoreNumberFromKeys(setScore, [
        "tieBreakB",
        "tie_break_b",
      ]);
      const hasTieBreakDecision =
        tieBreakA !== null && tieBreakB !== null && tieBreakA !== tieBreakB;
      const hasGames =
        gamesA !== null &&
        gamesB !== null &&
        !(gamesA === 0 && gamesB === 0 && hasTieBreakDecision);
      const recoveredGamesA =
        (!set.isMatchTieBreak || !hasRegularSets) &&
        !hasGames &&
        hasTieBreakDecision
          ? tieBreakA
          : null;
      const recoveredGamesB =
        (!set.isMatchTieBreak || !hasRegularSets) &&
        !hasGames &&
        hasTieBreakDecision
          ? tieBreakB
          : null;
      return {
        id: `${Number(set.setNumber || index + 1)}-${index}`,
        gamesA: hasGames
          ? gamesA
          : recoveredGamesA !== null
            ? recoveredGamesA
            : set.isMatchTieBreak &&
                Number(tieBreakA || 0) > Number(tieBreakB || 0)
              ? 1
              : 0,
        gamesB: hasGames
          ? gamesB
          : recoveredGamesB !== null
            ? recoveredGamesB
            : set.isMatchTieBreak &&
                Number(tieBreakB || 0) > Number(tieBreakA || 0)
              ? 1
              : 0,
      };
    });
}

function hasUsableScoreSets(
  sets: Array<{ id: string; gamesA: number; gamesB: number }>,
) {
  return sets.some((set) => Number(set.gamesA) > 0 || Number(set.gamesB) > 0);
}

function visibleScoreSetsLabel(
  sets: Array<{ id: string; gamesA: number; gamesB: number }>,
) {
  if (!hasUsableScoreSets(sets)) return "";
  return sets
    .map((set) => `${set.gamesA}x${set.gamesB}`)
    .join(siteTexts.challenges.publicTournamentProgress.scoreSeparator);
}

function matchFromTimelineSlot(
  slot: PublicTournamentScheduleSlot,
  status: string,
): PublicTournamentBracketMatch {
  return {
    id: slotMatchId(slot),
    stage: slot.stage || "",
    groupKey: slot.groupKey || null,
    groupName: slot.groupName || null,
    roundNumber: Number(slot.roundNumber || 0),
    roundCode: String(slot.roundCode || ""),
    matchNumber: Number(slot.matchNumber || 0),
    sideARegistrationId: slot.sideARegistrationId || null,
    sideBRegistrationId: slot.sideBRegistrationId || null,
    sideALabel: slot.sideALabel || null,
    sideBLabel: slot.sideBLabel || null,
    sideAPlayerAvatarUrl: slot.sideAPlayerAvatarUrl || null,
    sideAPartnerAvatarUrl: slot.sideAPartnerAvatarUrl || null,
    sideAPlayerPublicPlayerId: slot.sideAPlayerPublicPlayerId || null,
    sideAPartnerPublicPlayerId: slot.sideAPartnerPublicPlayerId || null,
    sideAPlayerSex: slot.sideAPlayerSex || null,
    sideAPartnerSex: slot.sideAPartnerSex || null,
    sideBPlayerAvatarUrl: slot.sideBPlayerAvatarUrl || null,
    sideBPartnerAvatarUrl: slot.sideBPartnerAvatarUrl || null,
    sideBPlayerPublicPlayerId: slot.sideBPlayerPublicPlayerId || null,
    sideBPartnerPublicPlayerId: slot.sideBPartnerPublicPlayerId || null,
    sideBPlayerSex: slot.sideBPlayerSex || null,
    sideBPartnerSex: slot.sideBPartnerSex || null,
    winnerSide: slot.winnerSide || null,
    status,
    sets: slot.sets || [],
  } as PublicTournamentBracketMatch;
}

function scoreSetsWithFallback({
  match,
  bracket,
  fallbackMatch,
  fallbackBracket,
}: {
  match: PublicTournamentBracketMatch;
  bracket: PublicTournamentBracket | null | undefined;
  fallbackMatch?: PublicTournamentBracketMatch | null;
  fallbackBracket?: PublicTournamentBracket | null;
}) {
  const primary = getVisibleScoreSets(match);
  if (hasUsableScoreSets(primary) || !fallbackMatch) return primary;
  const fallback = getVisibleScoreSets(fallbackMatch);
  return hasUsableScoreSets(fallback) ? fallback : primary;
}

function ScoreDisplay({
  sets,
  className = "",
}: {
  sets: Array<{ id: string; gamesA: number; gamesB: number }>;
  className?: string;
}) {
  if (sets.length === 0) {
    return <small className="public-tournament-score-empty">x</small>;
  }
  const scoreLabel = sets
    .map((set) => `${set.gamesA}-${set.gamesB}`)
    .join(", ");
  return (
    <strong
      className={`public-tournament-score${className ? ` ${className}` : ""}`}
      aria-label={siteTexts.challenges.publicTournamentProgress.matchResultAria.replace("{score}", scoreLabel)}
    >
      {sets.map((set) => (
        <span className="public-tournament-score-set" key={set.id}>
          <span className="public-tournament-score-number">{set.gamesA}</span>
          <span className="public-tournament-score-separator">-</span>
          <span className="public-tournament-score-number">{set.gamesB}</span>
        </span>
      ))}
    </strong>
  );
}

function SideScoreDisplay({
  sets,
  side,
}: {
  sets: Array<{ id: string; gamesA: number; gamesB: number }>;
  side: "A" | "B";
}) {
  if (sets.length === 0) return null;
  return (
    <span
      className="public-tournament-side-score"
      aria-label={siteTexts.challenges.publicTournamentProgress.sideResultAria.replace("{side}", side)}
    >
      {sets.map((set) => (
        <span
          className="public-tournament-score-number"
          key={`${set.id}-${side}`}
        >
          {side === "A" ? set.gamesA : set.gamesB}
        </span>
      ))}
    </span>
  );
}

function categoryLabel(categories: TournamentCategory[], key?: string | null) {
  const normalized = String(key || "").trim();
  return (
    categories.find((category) => category.categoryKey === normalized)
      ?.categoryName ||
    normalized ||
    progressTexts.categoryFallback
  );
}

function roundLabel(roundCode?: string | null, roundNumber?: number | null) {
  const normalized = String(roundCode || "").toLowerCase();
  if (normalized === "group" || normalized === "group_stage")
    return progressTexts.group;
  if (normalized === "play_in") return progressTexts.qualifying;
  if (normalized === "round_of_32") return progressTexts.roundOf32;
  if (normalized === "round_of_16") return progressTexts.roundOf16;
  if (normalized === "round_of_8" || normalized === "quarterfinal")
    return progressTexts.quarterfinal;
  if (normalized === "round_of_4" || normalized === "semifinal")
    return progressTexts.semifinal;
  if (normalized === "round_of_2" || normalized === "final")
    return progressTexts.final;
  return Number(roundNumber || 0) <= 1
    ? progressTexts.group
    : progressTexts.knockout;
}

function matchCodeForRound(
  roundCode?: string | null,
  matchNumber?: number | null,
) {
  const number = Math.max(1, Math.floor(Number(matchNumber || 1)));
  const normalized = String(roundCode || "").toLowerCase();
  if (normalized === "play_in") return `CL${number}`;
  if (normalized === "round_of_64") return `R64-${number}`;
  if (normalized === "round_of_32") return `R32-${number}`;
  if (normalized === "round_of_16") return `R16-${number}`;
  if (normalized === "round_of_8" || normalized === "quarterfinal")
    return `QF${number}`;
  if (normalized === "round_of_4" || normalized === "semifinal")
    return `SF${number}`;
  if (normalized === "round_of_2" || normalized === "final")
    return `F${number}`;
  return `J${number}`;
}

function toDateTimeLocal(value?: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString();
}

function dateKey(value?: string | null) {
  return toDateTimeLocal(value).slice(0, 10);
}

function todayDateKey() {
  return toDateTimeLocal(new Date().toISOString()).slice(0, 10);
}

function formatTime(value?: string | null) {
  return toDateTimeLocal(value).slice(11, 16);
}

function formatDateButton(value: string) {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return new Intl.DateTimeFormat(LOCALE_BY_LANGUAGE[getSiteLanguage()]).format(
    date,
  );
}

function formatElapsedSince(value: string | null | undefined, nowMs: number) {
  const startedAt = new Date(String(value || "")).getTime();
  if (!Number.isFinite(startedAt)) return "";
  const elapsedMinutes = Math.max(0, Math.floor((nowMs - startedAt) / 60000));
  const labels = siteTexts.challenges.publicTournamentProgress.elapsed;
  if (elapsedMinutes < 1) return labels.lessThanMinute;
  if (elapsedMinutes < 60) {
    return labels.minutes.replace("{minutes}", String(elapsedMinutes));
  }
  const hours = Math.floor(elapsedMinutes / 60);
  const minutes = elapsedMinutes % 60;
  if (hours < 24) {
    return minutes > 0
      ? labels.hoursMinutes
          .replace("{hours}", String(hours))
          .replace("{minutes}", String(minutes))
      : labels.hours.replace("{hours}", String(hours));
  }
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours > 0
    ? labels.daysHours
        .replace("{days}", String(days))
        .replace("{hours}", String(remainingHours))
    : labels.days.replace("{days}", String(days));
}

function inProgressElapsedLabel({
  status,
  actualStartedAt,
  nowMs,
}: {
  status?: string | null;
  actualStartedAt?: string | null;
  nowMs: number;
}) {
  return String(status || "")
    .trim()
    .toLowerCase() === "in_progress"
    ? formatElapsedSince(actualStartedAt, nowMs)
    : "";
}

function slotCourtLabel(value?: string | null) {
  const courtName = String(value || "").trim();
  return !courtName || courtName === "A definir"
    ? siteTexts.challenges.publicTournamentProgress.fallback.court
    : courtName;
}

function slotMatchId(
  slot: NonNullable<PublicTournamentSchedule["slots"]>[number],
) {
  const matchId = Number(slot.matchId || 0);
  return Number.isFinite(matchId) && matchId > 0 ? matchId : 0;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function blobFromCanvas(canvas: HTMLCanvasElement) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
        return;
      }
      reject(new Error(siteTexts.challenges.publicTournamentProgress.canvas.imageError));
    }, "image/png");
  });
}

function loadShareImage(src?: string | null) {
  const safeSrc = String(src || "").trim();
  if (!safeSrc) return Promise.resolve<HTMLImageElement | null>(null);
  return new Promise<HTMLImageElement | null>((resolve) => {
    const image = new Image();
    if (!safeSrc.startsWith("data:")) image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = safeSrc;
  });
}

function initialsFromName(value?: string | null) {
  const initials = String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((chunk) => chunk[0]?.toUpperCase() || "")
    .join("");
  return initials || "TN";
}

function splitPairNames(value?: string | null) {
  return String(value || "")
    .split(/\s*(?:\/|\+|&)\s*/)
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 2);
}

function clampCanvasTextToWidth({
  context,
  text,
  maxWidth,
}: {
  context: CanvasRenderingContext2D;
  text: string;
  maxWidth: number;
}) {
  const safeText = String(text || "").trim();
  if (!safeText || context.measureText(safeText).width <= maxWidth) {
    return safeText;
  }
  let next = safeText;
  while (
    next.length > 1 &&
    context.measureText(`${next}...`).width > maxWidth
  ) {
    next = next.slice(0, -1).trim();
  }
  return next ? `${next}...` : "...";
}

function fitFontSize({
  context,
  text,
  maxWidth,
  maxSize,
  minSize,
  weight = 900,
}: {
  context: CanvasRenderingContext2D;
  text: string;
  maxWidth: number;
  maxSize: number;
  minSize: number;
  weight?: number;
}) {
  for (let size = maxSize; size >= minSize; size -= 2) {
    context.font = `${weight} ${size}px system-ui, -apple-system, BlinkMacSystemFont, sans-serif`;
    if (context.measureText(text).width <= maxWidth) return size;
  }
  return minSize;
}

function drawCenteredFitText({
  context,
  text,
  x,
  y,
  maxWidth,
  maxSize,
  minSize,
  fillStyle,
  weight = 900,
}: {
  context: CanvasRenderingContext2D;
  text: string;
  x: number;
  y: number;
  maxWidth: number;
  maxSize: number;
  minSize: number;
  fillStyle: string | CanvasGradient;
  weight?: number;
}) {
  fitFontSize({ context, text, maxWidth, maxSize, minSize, weight });
  context.fillStyle = fillStyle;
  context.textAlign = "center";
  context.fillText(text, x, y);
}

function splitTextIntoLines({
  context,
  text,
  maxWidth,
  maxLines,
}: {
  context: CanvasRenderingContext2D;
  text: string;
  maxWidth: number;
  maxLines: number;
}) {
  const words = String(text || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return [""];
  const lines: string[] = [];
  let current = "";
  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (context.measureText(candidate).width <= maxWidth) {
      current = candidate;
      return;
    }
    if (current) lines.push(current);
    current = word;
  });
  if (current) lines.push(current);

  if (lines.length <= maxLines) {
    return lines.map((line) =>
      clampCanvasTextToWidth({ context, text: line, maxWidth }),
    );
  }
  const visible = lines.slice(0, maxLines);
  let last = visible[maxLines - 1] || "";
  while (last && context.measureText(`${last}...`).width > maxWidth) {
    last = last.split(/\s+/).slice(0, -1).join(" ");
    if (!last && visible[maxLines - 1]) {
      last = clampCanvasTextToWidth({
        context,
        text: visible[maxLines - 1],
        maxWidth,
      }).replace(/\.\.\.$/, "");
      break;
    }
    if (!last) break;
  }
  visible[maxLines - 1] = last
    ? clampCanvasTextToWidth({ context, text: `${last}...`, maxWidth })
    : "...";
  return visible.map((line) =>
    clampCanvasTextToWidth({ context, text: line, maxWidth }),
  );
}

function drawCenteredWrappedText({
  context,
  text,
  x,
  y,
  maxWidth,
  maxSize,
  minSize,
  fillStyle,
  maxLines = 2,
  lineHeight = 1.14,
  weight = 900,
}: {
  context: CanvasRenderingContext2D;
  text: string;
  x: number;
  y: number;
  maxWidth: number;
  maxSize: number;
  minSize: number;
  fillStyle: string | CanvasGradient;
  maxLines?: number;
  lineHeight?: number;
  weight?: number;
}) {
  let size = maxSize;
  let lines: string[] = [String(text || "")];
  while (size >= minSize) {
    context.font = `${weight} ${size}px system-ui, -apple-system, BlinkMacSystemFont, sans-serif`;
    lines = splitTextIntoLines({ context, text, maxWidth, maxLines });
    const fits =
      lines.length <= maxLines &&
      lines.every((line) => context.measureText(line).width <= maxWidth);
    if (fits) break;
    size -= 2;
  }

  if (size < minSize) {
    size = minSize;
    context.font = `${weight} ${size}px system-ui, -apple-system, BlinkMacSystemFont, sans-serif`;
    lines = splitTextIntoLines({ context, text, maxWidth, maxLines });
  }

  context.fillStyle = fillStyle;
  context.textAlign = "center";
  context.textBaseline = "alphabetic";
  const step = Math.round(size * lineHeight);
  const firstY = y - ((lines.length - 1) * step) / 2;
  lines.slice(0, maxLines).forEach((line, index) => {
    context.fillText(line, x, firstY + index * step);
  });
}

function drawLeftWrappedText({
  context,
  text,
  x,
  y,
  maxWidth,
  maxSize,
  minSize,
  fillStyle,
  maxLines = 2,
  lineHeight = 1.18,
  weight = 900,
}: {
  context: CanvasRenderingContext2D;
  text: string;
  x: number;
  y: number;
  maxWidth: number;
  maxSize: number;
  minSize: number;
  fillStyle: string | CanvasGradient;
  maxLines?: number;
  lineHeight?: number;
  weight?: number;
}) {
  let size = maxSize;
  let lines: string[] = [String(text || "")];
  while (size >= minSize) {
    context.font = `${weight} ${size}px system-ui, -apple-system, BlinkMacSystemFont, sans-serif`;
    lines = splitTextIntoLines({ context, text, maxWidth, maxLines });
    const fits =
      lines.length <= maxLines &&
      lines.every((line) => context.measureText(line).width <= maxWidth);
    if (fits) break;
    size -= 2;
  }

  if (size < minSize) {
    size = minSize;
    context.font = `${weight} ${size}px system-ui, -apple-system, BlinkMacSystemFont, sans-serif`;
    lines = splitTextIntoLines({ context, text, maxWidth, maxLines });
  }

  context.fillStyle = fillStyle;
  context.textAlign = "left";
  context.textBaseline = "alphabetic";
  const step = Math.round(size * lineHeight);
  lines.slice(0, maxLines).forEach((line, index) => {
    context.fillText(line, x, y + index * step);
  });
}

function drawNeonLine(
  context: CanvasRenderingContext2D,
  color: string,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
) {
  context.save();
  context.strokeStyle = color;
  context.lineWidth = 3;
  context.shadowColor = color;
  context.shadowBlur = 18;
  context.beginPath();
  context.moveTo(fromX, fromY);
  context.lineTo(toX, toY);
  context.stroke();
  context.restore();
}

function setCanvasLetterSpacing(
  context: CanvasRenderingContext2D,
  value: string,
) {
  (
    context as CanvasRenderingContext2D & { letterSpacing?: string }
  ).letterSpacing = value;
}

function drawScoreText(
  context: CanvasRenderingContext2D,
  score: string,
  x: number,
  y: number,
  pink: string,
  blue: string,
  strong: string,
) {
  const singleSet = String(score || "").match(/^\s*(\d+)\s*x\s*(\d+)\s*$/i);
  context.textAlign = "center";
  if (singleSet) {
    context.font =
      "900 118px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
    context.shadowBlur = 28;
    context.shadowColor = pink;
    context.fillStyle = pink;
    context.fillText(singleSet[1], x - 86, y);
    context.shadowBlur = 0;
    context.fillStyle = strong;
    context.font =
      "900 58px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
    context.fillText("x", x, y - 18);
    context.font =
      "900 118px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
    context.shadowBlur = 28;
    context.shadowColor = blue;
    context.fillStyle = blue;
    context.fillText(singleSet[2], x + 86, y);
    context.shadowBlur = 0;
    return;
  }
  drawCenteredFitText({
    context,
    text: score,
    x,
    y,
    maxWidth: 300,
    maxSize: 66,
    minSize: 34,
    fillStyle: strong,
  });
}

function drawCircleAvatar({
  context,
  image,
  initials,
  x,
  y,
  radius,
  pink,
  blue,
  strong,
  badge = false,
}: {
  context: CanvasRenderingContext2D;
  image: HTMLImageElement | null;
  initials: string;
  x: number;
  y: number;
  radius: number;
  pink: string;
  blue: string;
  strong: string;
  badge?: boolean;
}) {
  context.save();
  context.beginPath();
  context.arc(x, y, radius, 0, Math.PI * 2);
  context.clip();
  if (image) {
    const side = Math.min(image.width, image.height);
    context.drawImage(
      image,
      (image.width - side) / 2,
      (image.height - side) / 2,
      side,
      side,
      x - radius,
      y - radius,
      radius * 2,
      radius * 2,
    );
  } else {
    const fallback = context.createLinearGradient(
      x - radius,
      y - radius,
      x + radius,
      y + radius,
    );
    fallback.addColorStop(0, blue);
    fallback.addColorStop(1, pink);
    context.fillStyle = fallback;
    context.fillRect(x - radius, y - radius, radius * 2, radius * 2);
    context.fillStyle = strong;
    context.textAlign = "center";
    context.font = `900 ${Math.max(18, Math.floor(radius * 0.44))}px system-ui, -apple-system, BlinkMacSystemFont, sans-serif`;
    context.fillText(initials, x, y + radius * 0.16);
  }
  context.restore();

  const border = context.createLinearGradient(x - radius, y, x + radius, y);
  border.addColorStop(0, pink);
  border.addColorStop(1, blue);
  context.save();
  context.strokeStyle = border;
  context.lineWidth = badge ? 4 : 7;
  context.shadowBlur = badge ? 12 : 22;
  context.shadowColor = badge ? pink : blue;
  context.beginPath();
  context.arc(x, y, radius, 0, Math.PI * 2);
  context.stroke();
  context.restore();
}

async function createChampionShareBlob({
  champion,
  eventDate,
  eventLocation,
  eventTitle,
}: {
  champion: PublicTournamentChampionSummary;
  eventDate?: string | null;
  eventLocation?: string | null;
  eventTitle?: string | null;
}) {
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1920;
  const context = canvas.getContext("2d");
  if (!context) throw new Error(siteTexts.challenges.publicTournamentProgress.canvas.unavailableCanvas);

  const styles = getComputedStyle(document.documentElement);
  const pink =
    styles.getPropertyValue("--color-brand-pink").trim() || "#e53682";
  const blue =
    styles.getPropertyValue("--color-brand-blue").trim() || "#2b9cdf";
  const strong =
    styles.getPropertyValue("--color-challenge-text-strong").trim() ||
    "#f8fcff";
  const soft =
    styles.getPropertyValue("--color-challenge-text-soft").trim() ||
    "rgba(222,235,245,0.82)";
  const labels = getResultShareLabels(champion.categoryName);
  const championNames = splitPairNames(champion.championLabel);
  const runnerNames = splitPairNames(champion.runnersUpLabel);
  const championPlayers =
    championNames.length > 0 ? championNames : [champion.championLabel];
  const runnerPlayers =
    runnerNames.length > 0 ? runnerNames : [champion.runnersUpLabel];
  const [championImages, runnerImages] = await Promise.all([
    Promise.all(
      champion.championAvatarUrls
        .slice(0, 2)
        .map((avatarUrl) => loadShareImage(avatarUrl)),
    ),
    Promise.all(
      champion.runnersUpAvatarUrls
        .slice(0, 2)
        .map((avatarUrl) => loadShareImage(avatarUrl)),
    ),
  ]);

  const parsedDate = eventDate ? new Date(eventDate) : null;
  const hasValidDate = parsedDate && !Number.isNaN(parsedDate.getTime());
  const dateLocale = currentDateLocale();
  const dateLabel = hasValidDate
    ? new Intl.DateTimeFormat(dateLocale, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
        .format(parsedDate)
        .replace(".", "")
        .toUpperCase()
    : "";
  const timeLabel = hasValidDate
    ? new Intl.DateTimeFormat(dateLocale, {
        hour: "2-digit",
        minute: "2-digit",
      }).format(parsedDate)
    : "";
  const locationLabel = String(eventLocation || "")
    .split(/\s+-\s+/)
    .map((item) => item.trim())
    .filter(Boolean)
    .join(", ");

  const gradient = context.createLinearGradient(0, 0, 1080, 1920);
  gradient.addColorStop(0, "#020717");
  gradient.addColorStop(0.45, "#06152b");
  gradient.addColorStop(1, "#120018");
  context.fillStyle = gradient;
  context.fillRect(0, 0, 1080, 1920);

  context.fillStyle = "rgba(43, 156, 223, 0.15)";
  context.beginPath();
  context.arc(980, 1260, 760, 0, Math.PI * 2);
  context.fill();
  context.fillStyle = "rgba(229, 54, 130, 0.16)";
  context.beginPath();
  context.arc(-60, 440, 570, 0, Math.PI * 2);
  context.fill();
  for (let x = 36; x <= 230; x += 18) {
    for (let y = 24; y <= 230; y += 18) {
      context.fillStyle = `rgba(229, 54, 130, ${Math.max(0.02, 0.24 - x / 1100 - y / 1100)})`;
      context.beginPath();
      context.arc(x, y, 2, 0, Math.PI * 2);
      context.fill();
    }
  }
  for (let x = 860; x <= 1040; x += 18) {
    for (let y = 850; y <= 1140; y += 18) {
      context.fillStyle = `rgba(43, 156, 223, ${Math.max(0.02, 0.22 - (y - 850) / 1500)})`;
      context.beginPath();
      context.arc(x, y, 2, 0, Math.PI * 2);
      context.fill();
    }
  }
  drawNeonLine(context, pink, 0, 260, 112, 92);
  drawNeonLine(context, pink, 0, 515, 96, 405);
  drawNeonLine(context, blue, 1080, 180, 1008, 260);
  drawNeonLine(context, blue, 1080, 345, 972, 470);

  const logoGradient = context.createLinearGradient(360, 70, 720, 175);
  logoGradient.addColorStop(0, blue);
  logoGradient.addColorStop(0.45, "#9f6cff");
  logoGradient.addColorStop(1, pink);
  context.textAlign = "center";
  context.shadowBlur = 34;
  context.shadowColor = pink;
  context.fillStyle = logoGradient;
  context.font =
    "italic 900 140px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.fillText("TNP", 540, 155);
  context.shadowBlur = 0;
  context.fillStyle = blue;
  context.font =
    "800 30px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  setCanvasLetterSpacing(context, "8px");
  context.fillText("TODOS NO PADEL", 540, 205);
  setCanvasLetterSpacing(context, "0px");

  context.fillStyle = strong;
  context.font =
    "500 34px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  setCanvasLetterSpacing(context, "22px");
  context.fillText("TORNEIO", 540, 292);
  setCanvasLetterSpacing(context, "0px");
  const titleGradient = context.createLinearGradient(150, 340, 930, 340);
  titleGradient.addColorStop(0, strong);
  titleGradient.addColorStop(0.48, strong);
  titleGradient.addColorStop(0.55, pink);
  titleGradient.addColorStop(1, blue);
  drawCenteredFitText({
    context,
    text: String(eventTitle || progressTexts.tournamentFallback)
      .trim()
      .toUpperCase(),
    x: 540,
    y: 380,
    maxWidth: 900,
    maxSize: 78,
    minSize: 46,
    fillStyle: titleGradient,
  });

  context.strokeStyle = blue;
  context.fillStyle = "rgba(255, 255, 255, 0.04)";
  context.lineWidth = 3;
  context.beginPath();
  context.roundRect(410, 415, 260, 52, 16);
  context.fill();
  context.stroke();
  context.fillStyle = soft;
  context.font =
    "900 30px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.fillText(champion.categoryName.toUpperCase(), 540, 452);

  context.textAlign = "left";
  context.fillStyle = pink;
  context.font = "400 42px 'Material Symbols Outlined', system-ui";
  context.fillText("calendar_month", 230, 535);
  context.fillStyle = strong;
  context.font = "800 25px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.fillText(dateLabel || siteTexts.challenges.publicTournamentProgress.fallback.date, 305, 515);
  context.fillText(timeLabel || siteTexts.challenges.publicTournamentProgress.fallback.time, 305, 548);

  context.strokeStyle = "rgba(255,255,255,0.35)";
  context.lineWidth = 1.5;
  context.beginPath();
  context.moveTo(540, 500);
  context.lineTo(540, 568);
  context.stroke();

  context.fillStyle = blue;
  context.font = "400 46px 'Material Symbols Outlined', system-ui";
  context.fillText("location_on", 596, 540);
  if (locationLabel) {
    drawLeftWrappedText({
      context,
      text: locationLabel.toUpperCase(),
      x: 660,
      y: 516,
      maxWidth: 340,
      maxSize: 25,
      minSize: 20,
      fillStyle: strong,
      maxLines: 2,
      lineHeight: 1.28,
      weight: 900,
    });
  }

  const mainBorder = context.createLinearGradient(70, 610, 1010, 610);
  mainBorder.addColorStop(0, pink);
  mainBorder.addColorStop(1, blue);

  const drawPlayerName = ({
    name,
    x,
    y,
    maxWidth,
    maxSize,
    minSize,
    color = strong,
  }: {
    name: string;
    x: number;
    y: number;
    maxWidth: number;
    maxSize: number;
    minSize: number;
    color?: string | CanvasGradient;
  }) => {
    drawCenteredWrappedText({
      context,
      text: String(
        name || siteTexts.challenges.publicDetail.winnerShare.athleteFallback,
      ).toUpperCase(),
      x,
      y,
      maxWidth,
      maxSize,
      minSize,
      fillStyle: color,
      maxLines: 2,
      lineHeight: 1.05,
    });
  };

  const drawFeaturedPlayer = ({
    image,
    name,
    x,
  }: {
    image: HTMLImageElement | null;
    name: string;
    x: number;
  }) => {
    drawCircleAvatar({
      context,
      image,
      initials: initialsFromName(name),
      x,
      y: 902,
      radius: 120,
      pink,
      blue,
      strong,
    });
    drawPlayerName({
      name,
      x,
      y: 1088,
      maxWidth: 320,
      maxSize: 34,
      minSize: 22,
    });
  };

  const drawCompactTeam = ({
    images,
    names,
    title,
    centerX,
  }: {
    images: Array<HTMLImageElement | null>;
    names: string[];
    title: string;
    centerX: number;
  }) => {
    context.fillStyle = strong;
    context.font =
      "900 23px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
    context.textAlign = "center";
    context.fillText(title, centerX, 1288);
    const players =
      names.length > 0
        ? names.slice(0, 2)
        : [siteTexts.challenges.publicTournamentProgress.canvas.athleteFallback];
    const avatarGap = players.length > 1 ? 108 : 0;
    const firstX = centerX - avatarGap / 2;
    players.forEach((name, index) => {
      const avatarX = firstX + index * avatarGap;
      drawCircleAvatar({
        context,
        image: images[index] || null,
        initials: initialsFromName(name),
        x: avatarX,
        y: 1372,
        radius: 48,
        pink,
        blue,
        strong,
        badge: true,
      });
      drawPlayerName({
        name,
        x: avatarX,
        y: 1472,
        maxWidth: players.length > 1 ? 122 : 245,
        maxSize: 18,
        minSize: 13,
      });
    });
  };

  context.fillStyle = "rgba(6, 20, 43, 0.78)";
  context.strokeStyle = mainBorder;
  context.lineWidth = 3;
  context.beginPath();
  context.roundRect(72, 590, 936, 600, 56);
  context.fill();
  context.stroke();

  context.textAlign = "center";
  drawNeonLine(context, pink, 195, 700, 315, 700);
  drawNeonLine(context, blue, 765, 700, 885, 700);
  drawCenteredFitText({
    context,
    text: labels.title,
    x: 540,
    y: 720,
    maxWidth: 600,
    maxSize: 54,
    minSize: 38,
    fillStyle: strong,
  });

  drawFeaturedPlayer({
    image: championImages[0] || null,
    name: championPlayers[0] || champion.championLabel,
    x: 335,
  });
  drawFeaturedPlayer({
    image: championImages[1] || null,
    name: championPlayers[1] || championPlayers[0] || champion.championLabel,
    x: 745,
  });

  const badgeGradient = context.createLinearGradient(340, 1120, 740, 1120);
  badgeGradient.addColorStop(0, pink);
  badgeGradient.addColorStop(1, blue);
  context.fillStyle = badgeGradient;
  context.beginPath();
  context.roundRect(340, 1120, 400, 50, 10);
  context.fill();
  context.fillStyle = strong;
  context.font = "900 24px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.fillText(`${siteTexts.challenges.publicTournamentProgress.canvas.firstPlacePrefix} ${labels.championBadge}`, 540, 1153);

  context.fillStyle = "rgba(4, 13, 30, 0.86)";
  context.strokeStyle = mainBorder;
  context.lineWidth = 2;
  context.beginPath();
  context.roundRect(64, 1220, 952, 330, 30);
  context.fill();
  context.stroke();

  context.strokeStyle = "rgba(255,255,255,0.22)";
  context.beginPath();
  context.moveTo(388, 1260);
  context.lineTo(388, 1510);
  context.moveTo(692, 1260);
  context.lineTo(692, 1510);
  context.stroke();

  drawCompactTeam({
    images: runnerImages,
    names: runnerPlayers,
    title: labels.runnerTitle,
    centerX: 226,
  });

  context.fillStyle = strong;
  context.font =
    "500 34px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  setCanvasLetterSpacing(context, "14px");
  context.fillText(
    siteTexts.challenges.publicDetail.winnerShare.final,
    540,
    1308,
  );
  setCanvasLetterSpacing(context, "0px");
  drawScoreText(context, champion.finalScore, 540, 1428, pink, blue, strong);

  drawCompactTeam({
    images: championImages,
    names: championPlayers,
    title: labels.championSideTitle,
    centerX: 854,
  });

  context.textAlign = "center";
  context.fillStyle = pink;
  context.font = "400 58px 'Material Symbols Outlined', system-ui";
  context.fillText("send", 150, 1652);
  context.strokeStyle = "rgba(255,255,255,0.25)";
  context.beginPath();
  context.moveTo(390, 1600);
  context.lineTo(390, 1690);
  context.moveTo(690, 1600);
  context.lineTo(690, 1690);
  context.stroke();
  context.fillStyle = strong;
  context.font = "900 30px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.fillText(siteTexts.challenges.publicTournamentProgress.canvas.shareLine1, 280, 1628);
  context.fillText(siteTexts.challenges.publicTournamentProgress.canvas.shareLine2, 280, 1664);
  context.fillStyle = pink;
  context.font = "900 19px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.fillText(siteTexts.challenges.publicTournamentProgress.canvas.markInstagram, 280, 1694);
  context.fillStyle = soft;
  context.font =
    "600 25px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  setCanvasLetterSpacing(context, "7px");
  context.fillText(siteTexts.challenges.publicTournamentProgress.canvas.experienceLine1, 820, 1635);
  context.fillStyle = strong;
  context.font = "900 28px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.fillText(siteTexts.challenges.publicTournamentProgress.canvas.experienceLine2, 820, 1682);
  setCanvasLetterSpacing(context, "0px");

  context.strokeStyle = "rgba(255,255,255,0.18)";
  context.beginPath();
  context.moveTo(0, 1778);
  context.lineTo(1080, 1778);
  context.stroke();
  context.fillStyle = strong;
  context.font =
    "900 22px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.textAlign = "left";
  context.fillText(siteTexts.challenges.publicTournamentProgress.canvas.socialLine, 70, 1838);
  context.textAlign = "center";
  context.font =
    "900 24px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  setCanvasLetterSpacing(context, "9px");
  context.fillText("TNPPADEL.COM.BR", 540, 1838);
  setCanvasLetterSpacing(context, "0px");
  context.textAlign = "right";
  context.font =
    "900 42px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.fillText("TNP", 1010, 1842);

  return blobFromCanvas(canvas);
}

function formatGroupKey(value: string) {
  const normalized = String(value || "")
    .trim()
    .replace(/^grupo[-_\s]*/i, "");
  const groupLabel = siteTexts.challenges.publicTournamentProgress.round.group;
  return normalized ? `${groupLabel} ${normalized.toUpperCase()}` : groupLabel;
}

function timelinePhaseLabel(
  slot: NonNullable<PublicTournamentSchedule["slots"]>[number],
) {
  const groupName = String(slot.groupName || "").trim();
  if (groupName) return groupName;
  const stage = String(slot.stage || "")
    .trim()
    .toLowerCase();
  const roundCode = String(slot.roundCode || "")
    .trim()
    .toLowerCase();
  if (
    stage === "group" ||
    roundCode === "group" ||
    roundCode === "group_stage"
  ) {
    const groupKey = String(slot.groupKey || "").trim();
    return groupKey
      ? formatGroupKey(groupKey)
      : siteTexts.challenges.publicTournamentProgress.round.group;
  }
  return roundLabel(slot.roundCode, slot.roundNumber);
}

function isGroupStageMatch(match: PublicTournamentBracketMatch) {
  const stage = String(match.stage || "")
    .trim()
    .toLowerCase();
  const roundCode = String(match.roundCode || "")
    .trim()
    .toLowerCase();
  return (
    stage === "group" || roundCode === "group" || roundCode === "group_stage"
  );
}

function isByeMatch(match: PublicTournamentBracketMatch) {
  return (
    String(match.decidedBy || "").trim().toLowerCase() === "bye" ||
    Boolean(match.sideAIsBye) ||
    Boolean(match.sideBIsBye)
  );
}

function shiftDate(value: string, days: number) {
  const date = new Date(`${value}T00:00`);
  if (Number.isNaN(date.getTime())) return value;
  date.setDate(date.getDate() + days);
  return toDateTimeLocal(date.toISOString()).slice(0, 10);
}

export default function PublicTournamentProgressPanel({
  tournamentId,
  token,
  categories,
  mode,
  eventTitle,
  eventDate,
  eventLocation,
  selectedCategoryKey: controlledCategoryKey,
  onSelectedCategoryKeyChange,
  showInternalCategoryTabs = true,
}: PublicTournamentProgressPanelProps) {
  const [selectedCategoryKey, setSelectedCategoryKey] = useState("");
  const [timelineCategoryFilter, setTimelineCategoryFilter] = useState("all");
  const [timelineCourtFilter, setTimelineCourtFilter] = useState("all");
  const [timelineDateFilter, setTimelineDateFilter] = useState("");
  const [brackets, setBrackets] = useState<
    Record<string, PublicTournamentBracket | null>
  >({});
  const [schedule, setSchedule] = useState<PublicTournamentSchedule | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [sharingChampionImage, setSharingChampionImage] = useState(false);
  const [sharePreview, setSharePreview] = useState<SharePreviewState>(null);
  useBodyScrollLock(Boolean(sharePreview));
  useSiteLanguage();
  const t = siteTexts.challenges.publicTournamentProgress;

  const categoryKey = useMemo(() => {
    const normalizedControlled = String(controlledCategoryKey || "").trim();
    if (
      normalizedControlled &&
      categories.some(
        (category) => category.categoryKey === normalizedControlled,
      )
    ) {
      return normalizedControlled;
    }
    const normalizedInternal = String(selectedCategoryKey || "").trim();
    if (
      normalizedInternal &&
      categories.some((category) => category.categoryKey === normalizedInternal)
    ) {
      return normalizedInternal;
    }
    return String(categories[0]?.categoryKey || "").trim();
  }, [categories, controlledCategoryKey, selectedCategoryKey]);

  useEffect(() => {
    if (!categoryKey || controlledCategoryKey) return;
    setSelectedCategoryKey(categoryKey);
  }, [categoryKey, controlledCategoryKey]);

  const updateCategoryKey = useCallback(
    (nextCategoryKey: string) => {
      const normalized = String(nextCategoryKey || "").trim();
      if (onSelectedCategoryKeyChange) {
        onSelectedCategoryKeyChange(normalized);
        return;
      }
      setSelectedCategoryKey(normalized);
    },
    [onSelectedCategoryKeyChange],
  );

  const load = useCallback(async () => {
    if (!tournamentId || categories.length === 0) {
      setBrackets({});
      setSchedule(null);
      return;
    }
    setLoading(true);
    try {
      const [scheduleResult, bracketEntries] = await Promise.all([
        getPublicTournamentSchedule(tournamentId, token).catch(() => null),
        Promise.all(
          categories.map((category) =>
            getPublicTournamentBracket(
              tournamentId,
              category.categoryKey,
              token,
            )
              .then((bracket) => [category.categoryKey, bracket] as const)
              .catch(() => [category.categoryKey, null] as const),
          ),
        ),
      ]);
      setSchedule(scheduleResult);
      setBrackets(Object.fromEntries(bracketEntries));
    } finally {
      setLoading(false);
    }
  }, [categories, token, tournamentId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    return () => {
      if (sharePreview?.url) URL.revokeObjectURL(sharePreview.url);
    };
  }, [sharePreview?.url]);

  const scheduledByMatchId = useMemo(() => {
    const map = new Map<
      number,
      NonNullable<PublicTournamentSchedule["slots"]>[number]
    >();
    (schedule?.slots || []).forEach((slot) => {
      const matchId = slotMatchId(slot);
      if (matchId > 0) map.set(matchId, slot);
    });
    return map;
  }, [schedule?.slots]);

  const bracketMatchById = useMemo(() => {
    const map = new Map<
      number,
      {
        bracket: PublicTournamentBracket;
        match: PublicTournamentBracketMatch;
      }
    >();
    Object.values(brackets).forEach((bracket) => {
      if (!bracket) return;
      bracket.rounds.forEach((round) => {
        round.matches.forEach((match) => {
          map.set(Number(match.id), { bracket, match });
        });
      });
    });
    return map;
  }, [brackets]);

  const activeBracket = categoryKey ? brackets[categoryKey] || null : null;

  const bracketBlocks = useMemo<PublicBracketBlock[]>(() => {
    if (!activeBracket) return [];
    const allMatches = activeBracket.rounds
      .flatMap((round) => round.matches)
      .sort((left, right) => {
        if (left.roundNumber !== right.roundNumber) {
          return Number(left.roundNumber || 0) - Number(right.roundNumber || 0);
        }
        return Number(left.matchNumber || 0) - Number(right.matchNumber || 0);
      });
    const consumedMatchIds = new Set<number>();
    const blocks: PublicBracketBlock[] = [];
    const identityByRegistrationId = new Map<
      number,
      {
        avatarUrls: Array<string | null | undefined>;
        sexes: Array<string | null | undefined>;
        profileHrefs: Array<string | undefined>;
      }
    >();
    allMatches.forEach((match) => {
      const sideAId = Number(match.sideARegistrationId || 0);
      const sideBId = Number(match.sideBRegistrationId || 0);
      if (sideAId > 0) {
        identityByRegistrationId.set(sideAId, {
          avatarUrls: [
            match.sideAPlayerAvatarUrl,
            match.sideAPartnerAvatarUrl,
          ],
          sexes: [match.sideAPlayerSex, match.sideAPartnerSex],
          profileHrefs: [
            publicPlayerHref(match.sideAPlayerPublicPlayerId),
            publicPlayerHref(match.sideAPartnerPublicPlayerId),
          ],
        });
      }
      if (sideBId > 0) {
        identityByRegistrationId.set(sideBId, {
          avatarUrls: [
            match.sideBPlayerAvatarUrl,
            match.sideBPartnerAvatarUrl,
          ],
          sexes: [match.sideBPlayerSex, match.sideBPartnerSex],
          profileHrefs: [
            publicPlayerHref(match.sideBPlayerPublicPlayerId),
            publicPlayerHref(match.sideBPartnerPublicPlayerId),
          ],
        });
      }
    });

    (activeBracket.metadata?.groups || []).forEach((group) => {
      const groupId = String(group.id || "").trim();
      const groupName = String(group.name || groupId || t.round.group).trim();
      const matches = allMatches.filter((match) => {
        const matchGroupKey = String(match.groupKey || "").trim();
        const matchGroupName = String(match.groupName || "").trim();
        return (
          isGroupStageMatch(match) &&
          (matchGroupKey === groupId || matchGroupName === groupName)
        );
      });
      matches.forEach((match) => consumedMatchIds.add(Number(match.id)));
      blocks.push({
        id: `group-${groupId || groupName}`,
        title: groupName,
        subtitle: t.round.groupStage,
        kind: "group",
        isSeedHead: Boolean(group.isSeedHead),
        teams: group.teams
          .map((team) => {
            const identity = identityByRegistrationId.get(
              Number(team.registrationId || 0),
            );
            return {
              registrationId: Number(team.registrationId || 0),
              label: String(team.label || "").trim(),
              avatarUrls: identity?.avatarUrls || [
                team.playerAvatarUrl,
                team.partnerAvatarUrl,
              ],
              sexes: identity?.sexes || [],
              profileHrefs: identity?.profileHrefs || [],
              isSeedHead: Boolean(team.isSeedHead),
              seedRank: team.seedRank ?? null,
            };
          })
          .filter((team) => Boolean(team.label)),
        matches: matches.filter((match) => !isByeMatch(match)),
      });
    });

    const remainingByRound = new Map<string, PublicBracketBlock>();
    allMatches
      .filter((match) => !consumedMatchIds.has(Number(match.id)))
      .forEach((match) => {
        if (isByeMatch(match)) return;
        const key = `round-${match.roundNumber}-${match.roundCode}`;
        if (!remainingByRound.has(key)) {
          remainingByRound.set(key, {
            id: key,
            title: roundLabel(match.roundCode, match.roundNumber),
            subtitle: t.round.knockout,
            kind: "round",
            teams: [],
            matches: [],
          });
        }
        remainingByRound.get(key)?.matches.push(match);
      });

    return [...blocks, ...Array.from(remainingByRound.values())].filter(
      (block) => block.teams.length > 0 || block.matches.length > 0,
    );
  }, [activeBracket, t.round.group, t.round.groupStage, t.round.knockout]);

  const groupBracketBlocks = useMemo(
    () => bracketBlocks.filter((block) => block.kind === "group"),
    [bracketBlocks],
  );

  const roundBracketBlocks = useMemo(
    () => bracketBlocks.filter((block) => block.kind !== "group"),
    [bracketBlocks],
  );

  const championSummary =
    useMemo<PublicTournamentChampionSummary | null>(() => {
      if (!activeBracket) return null;
      const finalMatches = activeBracket.rounds
        .flatMap((round) => round.matches)
        .filter((match) => isFinalMatch(match))
        .sort((left, right) => {
          if (left.roundNumber !== right.roundNumber) {
            return (
              Number(right.roundNumber || 0) - Number(left.roundNumber || 0)
            );
          }
          return Number(right.matchNumber || 0) - Number(left.matchNumber || 0);
        });

      for (const match of finalMatches) {
        const scheduled = scheduledByMatchId.get(Number(match.id));
        const status = bracketMatchStatus(match, scheduled);
        if (matchStatusKey(status) !== "finished" || !match.winnerSide) {
          continue;
        }
        const championLabel =
          match.winnerSide === "A" ? match.sideALabel : match.sideBLabel;
        const runnersUpLabel =
          match.winnerSide === "A" ? match.sideBLabel : match.sideALabel;
        const finalScore = formatFinalScoreLabel(match, activeBracket);
        if (
          !String(championLabel || "").trim() ||
          !String(runnersUpLabel || "").trim() ||
          !String(finalScore || "").trim()
        ) {
          continue;
        }
        const championAvatarUrls =
          match.winnerSide === "A"
            ? [match.sideAPlayerAvatarUrl, match.sideAPartnerAvatarUrl]
            : [match.sideBPlayerAvatarUrl, match.sideBPartnerAvatarUrl];
        const runnersUpAvatarUrls =
          match.winnerSide === "A"
            ? [match.sideBPlayerAvatarUrl, match.sideBPartnerAvatarUrl]
            : [match.sideAPlayerAvatarUrl, match.sideAPartnerAvatarUrl];
        const championSexes =
          match.winnerSide === "A"
            ? [match.sideAPlayerSex, match.sideAPartnerSex]
            : [match.sideBPlayerSex, match.sideBPartnerSex];
        const runnersUpSexes =
          match.winnerSide === "A"
            ? [match.sideBPlayerSex, match.sideBPartnerSex]
            : [match.sideAPlayerSex, match.sideAPartnerSex];
        const championProfileHrefs =
          match.winnerSide === "A"
            ? [
                publicPlayerHref(match.sideAPlayerPublicPlayerId),
                publicPlayerHref(match.sideAPartnerPublicPlayerId),
              ]
            : [
                publicPlayerHref(match.sideBPlayerPublicPlayerId),
                publicPlayerHref(match.sideBPartnerPublicPlayerId),
              ];
        return {
          championLabel: String(championLabel).trim(),
          runnersUpLabel: String(runnersUpLabel).trim(),
          categoryName: categoryLabel(
            categories,
            activeBracket.categoryKey || categoryKey,
          ),
          finalScore,
          championAvatarUrls,
          runnersUpAvatarUrls,
          championSexes,
          runnersUpSexes,
          championProfileHrefs,
        };
      }
      return null;
    }, [activeBracket, categories, categoryKey, scheduledByMatchId]);

  const onShareChampion = useCallback(async () => {
    if (!championSummary || sharingChampionImage) return;
    setSharingChampionImage(true);
    try {
      const blob = await createChampionShareBlob({
        champion: championSummary,
        eventDate,
        eventLocation,
        eventTitle,
      });
      const filename = `resultado-${String(eventTitle || "torneio")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")}-${
        championSummary.categoryName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "") || "categoria"
      }.png`;
      setSharePreview((current) => {
        if (current?.url) URL.revokeObjectURL(current.url);
        return {
          blob,
          filename,
          url: URL.createObjectURL(blob),
        };
      });
    } finally {
      setSharingChampionImage(false);
    }
  }, [
    championSummary,
    eventDate,
    eventLocation,
    eventTitle,
    sharingChampionImage,
  ]);

  const onDownloadSharePreview = useCallback(() => {
    if (!sharePreview) return;
    downloadBlob(sharePreview.blob, sharePreview.filename);
  }, [sharePreview]);

  const onNativeSharePreview = useCallback(async () => {
    if (!sharePreview || !championSummary) return;
    const file = new File([sharePreview.blob], sharePreview.filename, {
      type: "image/png",
    });
    const sharePayload = {
      files: [file],
      title: t.sharePreview.nativeTitle,
      text: t.sharePreview.nativeText
        .replace("{champion}", championSummary.championLabel)
        .replace("{category}", championSummary.categoryName),
    };
    if (navigator.share && navigator.canShare?.(sharePayload)) {
      await navigator.share(sharePayload);
      return;
    }
    downloadBlob(sharePreview.blob, sharePreview.filename);
  }, [
    championSummary,
    sharePreview,
    t.sharePreview.nativeText,
    t.sharePreview.nativeTitle,
  ]);

  const closeSharePreview = useCallback(() => {
    setSharePreview((current) => {
      if (current?.url) URL.revokeObjectURL(current.url);
      return null;
    });
  }, []);

  const publishedTimelineSlots = useMemo(() => {
    const publishedCategoryKeys = new Set(
      Object.entries(brackets)
        .filter(([, bracket]) => Boolean(bracket))
        .map(([key]) => key),
    );
    return (schedule?.slots || []).filter((slot) => {
      const slotCategoryKey = String(slot.categoryKey || "").trim();
      return slotMatchId(slot) > 0 && publishedCategoryKeys.has(slotCategoryKey);
    });
  }, [brackets, schedule?.slots]);

  const timelineDates = useMemo(() => {
    return Array.from(
      new Set(
        publishedTimelineSlots
          .map((slot) => dateKey(slot.slotStartAt))
          .filter(Boolean),
      ),
    ).sort();
  }, [publishedTimelineSlots]);
  const hasGeneratedTimeline = timelineDates.length > 0;

  useEffect(() => {
    if (timelineDates.length === 0) {
      if (timelineDateFilter) setTimelineDateFilter("");
      return;
    }
    if (!timelineDateFilter || !timelineDates.includes(timelineDateFilter)) {
      const today = todayDateKey();
      setTimelineDateFilter(
        timelineDates.includes(today) ? today : timelineDates[0],
      );
    }
  }, [timelineDateFilter, timelineDates]);

  const timelineCourts = useMemo(() => {
    return Array.from(
      new Set(
        publishedTimelineSlots
          .map((slot) => String(slot.courtName || "").trim())
          .filter((courtName) => courtName && courtName !== "A definir"),
      ),
    ).sort((a, b) => a.localeCompare(b));
  }, [publishedTimelineSlots]);

  const timelineSlots = useMemo(
    () =>
      [...publishedTimelineSlots]
        .filter((slot) => {
          if (
            timelineCategoryFilter !== "all" &&
            String(slot.categoryKey || "").trim() !== timelineCategoryFilter
          ) {
            return false;
          }
          if (
            timelineCourtFilter !== "all" &&
            slotCourtLabel(slot.courtName) !== timelineCourtFilter
          ) {
            return false;
          }
          if (
            timelineDateFilter &&
            dateKey(slot.slotStartAt) !== timelineDateFilter
          ) {
            return false;
          }
          return true;
        })
        .sort((a, b) => {
          const timeDiff =
            new Date(a.slotStartAt).getTime() -
            new Date(b.slotStartAt).getTime();
          if (timeDiff !== 0) return timeDiff;
          return Number(a.id || 0) - Number(b.id || 0);
        }),
    [
      publishedTimelineSlots,
      timelineCategoryFilter,
      timelineCourtFilter,
      timelineDateFilter,
    ],
  );

  const canGoPreviousDay = timelineDates.includes(
    shiftDate(timelineDateFilter, -1),
  );
  const canGoNextDay = timelineDates.includes(shiftDate(timelineDateFilter, 1));

  const renderBracketBlock = (block: PublicBracketBlock) => {
    const showTeamList = block.teams.length > 0;
    return (
    <section
      key={block.id}
      className={`public-tournament-bracket-block${block.kind === "group" ? " is-group" : ""}${
        block.kind === "group" && !showTeamList ? " is-compact-group" : ""
      }`}
    >
      <div className="public-tournament-bracket-block-head">
        <div>
          <h4>{block.title}</h4>
          <span>{block.subtitle}</span>
        </div>
        <strong>{block.matches.length} {t.matchesSuffix}</strong>
      </div>

      {showTeamList && (
        <div className="public-tournament-bracket-teams">
          {block.teams.map((team) => (
            <PublicTournamentMatchSide
              key={`${block.id}-${team.registrationId || team.label}`}
              avatarSexes={team.sexes}
              avatarUrls={team.avatarUrls}
              className="public-tournament-group-team"
              label={team.label}
              profileHrefs={team.profileHrefs}
              showAvatars
            />
          ))}
        </div>
      )}

      <div className="public-tournament-bracket-match-list">
        {block.matches.length === 0 ? (
          <p className="public-tournament-match-meta">
            {t.empty.noBracketMatches}
          </p>
        ) : (
          block.matches.map((match) => {
            const scheduled = scheduledByMatchId.get(match.id);
            const status = bracketMatchStatus(match, scheduled);
            const statusKey = matchStatusKey(status);
            const elapsedLabel = inProgressElapsedLabel({
              status,
              actualStartedAt: scheduled?.actualStartedAt,
              nowMs,
            });
            const courtName = scheduled
              ? slotCourtLabel(scheduled.courtName)
              : t.fallback.court;
            const scoreSets = scoreSetsWithFallback({
              match,
              bracket: activeBracket,
              fallbackMatch: scheduled
                ? matchFromTimelineSlot(scheduled, status)
                : null,
              fallbackBracket: activeBracket,
            });
            const scoreLabel = visibleScoreSetsLabel(scoreSets);
            const matchCode =
              block.kind === "group"
                ? `${block.title} · Jogo ${match.matchNumber}`
                : matchCodeForRound(match.roundCode, match.matchNumber);
            return (
              <article
                key={match.id}
                className={`public-tournament-bracket-match is-${statusKey}`}
              >
                <div className="public-tournament-bracket-match-code">
                  <strong>{matchCode}</strong>
                  <span className="public-tournament-status">
                    {matchStatusLabel(status)}
                  </span>
                </div>
                <div className="public-tournament-bracket-match-line">
                  <span className={match.winnerSide === "A" ? "is-winner" : ""}>
                    {match.sideALabel || t.fallback.side}
                  </span>
                  <strong>x</strong>
                  <span className={match.winnerSide === "B" ? "is-winner" : ""}>
                    {match.sideBLabel || t.fallback.side}
                  </span>
                </div>
                <p className="public-tournament-bracket-schedule">
                  {scoreLabel && <strong>{scoreLabel}</strong>}
                  <span>
                    {scheduled
                      ? formatDateTimeLabel(scheduled.slotStartAt)
                      : t.fallback.time}
                  </span>
                  <strong>{courtName}</strong>
                  {elapsedLabel && (
                    <small className="public-tournament-elapsed">
                      {elapsedLabel}
                    </small>
                  )}
                </p>
              </article>
            );
          })
        )}
      </div>
    </section>
    );
  };

  return (
    <>
    <section className="challenge-public-panel public-tournament-progress">
      <header className="challenge-public-panel-head">
        <h3>{mode === "brackets" ? t.headers.bracketsTitle : t.headers.timelineTitle}</h3>
        <p>
          {mode === "brackets"
            ? t.headers.bracketsDescription
            : t.headers.timelineDescription}
        </p>
      </header>

      {mode === "brackets" && categories.length > 0 && showInternalCategoryTabs && (
        <div className="challenge-public-registration-tabs" role="tablist">
          {categories.map((category) => (
            <button
              key={`public-tournament-progress-category-${category.categoryKey}`}
              type="button"
              className={`challenge-public-registration-tab${
                categoryKey === category.categoryKey ? " is-active" : ""
              }`}
              onClick={() => updateCategoryKey(category.categoryKey)}
            >
              {category.categoryName}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <Spinner label={t.loadingTournament} />
      ) : mode === "brackets" ? (
        !activeBracket ? (
          <div className="challenge-public-empty-state">
            <h4>{t.empty.bracketTitle}</h4>
            <p>{t.empty.bracketText}</p>
          </div>
        ) : (
          <div className="public-tournament-bracket-stack">
            {championSummary && (
              <section className="public-tournament-champion-banner">
                <span className="public-tournament-champion-icon" aria-hidden="true">
                  <TrophyIcon />
                </span>
                <div className="public-tournament-champion-copy">
                  <span>{t.champion.label}</span>
                  <PublicTournamentMatchSide
                    avatarSize={42}
                    avatarSexes={championSummary.championSexes}
                    avatarUrls={championSummary.championAvatarUrls}
                    className="public-tournament-champion-pair"
                    label={championSummary.championLabel}
                    profileHrefs={championSummary.championProfileHrefs}
                    showAvatars
                  />
                  <small>{championSummary.categoryName}</small>
                  {championSummary.finalScore && (
                    <small>{t.champion.finalPrefix} {championSummary.finalScore}</small>
                  )}
                </div>
                <button
                  type="button"
                  className="public-tournament-share-button"
                  onClick={onShareChampion}
                  disabled={sharingChampionImage}
                >
                  <span className="material-symbols-outlined" aria-hidden="true">
                    ios_share
                  </span>
                  {sharingChampionImage
                    ? t.actions.generatingArt
                    : t.actions.shareResult}
                </button>
              </section>
            )}
            {groupBracketBlocks.length > 0 && (
              <div className="public-tournament-bracket-group-grid">
                {groupBracketBlocks.map(renderBracketBlock)}
              </div>
            )}
            {roundBracketBlocks.length > 0 && (
              <div className="public-tournament-bracket-round-stack">
                {roundBracketBlocks.map(renderBracketBlock)}
              </div>
            )}
          </div>
        )
      ) : (
        <>
          <div className="public-tournament-timeline-toolbar">
            <label>
              <span>{t.filters.category}</span>
              <select
                value={timelineCategoryFilter}
                onChange={(event) =>
                  setTimelineCategoryFilter(event.target.value)
                }
              >
                <option value="all">{t.filters.allCategories}</option>
                {categories.map((category) => (
                  <option
                    key={category.categoryKey}
                    value={category.categoryKey}
                  >
                    {category.categoryName}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>{t.filters.court}</span>
              <select
                value={timelineCourtFilter}
                onChange={(event) => setTimelineCourtFilter(event.target.value)}
              >
                <option value="all">{t.filters.allCourts}</option>
                {timelineCourts.map((court) => (
                  <option key={court} value={court}>
                    {court}
                  </option>
                ))}
              </select>
            </label>
            <div className="public-tournament-day-nav">
              <button
                type="button"
                disabled={!canGoPreviousDay}
                onClick={() =>
                  setTimelineDateFilter(shiftDate(timelineDateFilter, -1))
                }
                aria-label={t.filters.previousDay}
              >
                ‹
              </button>
              <time>{formatDateButton(timelineDateFilter)}</time>
              <button
                type="button"
                disabled={!canGoNextDay}
                onClick={() =>
                  setTimelineDateFilter(shiftDate(timelineDateFilter, 1))
                }
                aria-label={t.filters.nextDay}
              >
                ›
              </button>
            </div>
          </div>

          {timelineSlots.length === 0 ? (
            <div className="challenge-public-empty-state">
              <h4>
                {hasGeneratedTimeline
                  ? t.empty.noGamesTitle
                  : t.empty.timelineNotGeneratedTitle}
              </h4>
              <p>
                {hasGeneratedTimeline
                  ? t.empty.noGamesText
                  : t.empty.timelineNotGeneratedText}
              </p>
            </div>
          ) : (
            <div className="public-tournament-timeline">
              {timelineSlots.map((slot) => {
                const status = timelineSlotStatus(slot);
                const statusKey = matchStatusKey(status);
                const matchEntry = bracketMatchById.get(slotMatchId(slot));
                const slotMatch = matchFromTimelineSlot(slot, status);
                const timelineMatch = matchEntry?.match || slotMatch;
                const timelineBracket =
                  matchEntry?.bracket ||
                  brackets[String(slot.categoryKey || "").trim()] ||
                  activeBracket;
                const scoreSets = scoreSetsWithFallback({
                  match: timelineMatch,
                  bracket: timelineBracket,
                  fallbackMatch: slotMatch,
                  fallbackBracket: timelineBracket,
                });
                const elapsedLabel = inProgressElapsedLabel({
                  status,
                  actualStartedAt: slot.actualStartedAt,
                  nowMs,
                });
                return (
                  <article
                    key={slot.id}
                    className={`public-tournament-timeline-item is-${statusKey}`}
                  >
                    <div
                      className="public-tournament-timeline-marker"
                      aria-hidden="true"
                    />
                    <div className="public-tournament-timeline-mobile-card">
                      <div className="public-tournament-timeline-mobile-meta">
                        <strong>{formatTime(slot.slotStartAt)}</strong>
                        <small aria-hidden="true">·</small>
                        <span>{slotCourtLabel(slot.courtName)}</span>
                      </div>
                      <div className="public-tournament-timeline-mobile-phase">
                        {categoryLabel(categories, slot.categoryKey)}{t.scoreSeparator}
                        {timelinePhaseLabel(slot)}
                      </div>
                      <div className="public-tournament-timeline-mobile-sides">
                        <div className="public-tournament-timeline-mobile-side">
                          <PublicTournamentMatchSide
                            avatarSexes={[
                              timelineMatch.sideAPlayerSex,
                              timelineMatch.sideAPartnerSex,
                            ]}
                            avatarUrls={[
                              timelineMatch.sideAPlayerAvatarUrl,
                              timelineMatch.sideAPartnerAvatarUrl,
                            ]}
                            label={timelineMatch.sideALabel || t.fallback.side}
                            profileHrefs={[
                              publicPlayerHref(
                                timelineMatch.sideAPlayerPublicPlayerId,
                              ),
                              publicPlayerHref(
                                timelineMatch.sideAPartnerPublicPlayerId,
                              ),
                            ]}
                            showAvatars={
                              Number(timelineMatch.sideARegistrationId || 0) > 0
                            }
                          />
                        </div>
                        <div className="public-tournament-timeline-mobile-versus">
                          x
                        </div>
                        <div className="public-tournament-timeline-mobile-side">
                          <PublicTournamentMatchSide
                            avatarSexes={[
                              timelineMatch.sideBPlayerSex,
                              timelineMatch.sideBPartnerSex,
                            ]}
                            avatarUrls={[
                              timelineMatch.sideBPlayerAvatarUrl,
                              timelineMatch.sideBPartnerAvatarUrl,
                            ]}
                            label={timelineMatch.sideBLabel || t.fallback.side}
                            profileHrefs={[
                              publicPlayerHref(
                                timelineMatch.sideBPlayerPublicPlayerId,
                              ),
                              publicPlayerHref(
                                timelineMatch.sideBPartnerPublicPlayerId,
                              ),
                            ]}
                            showAvatars={
                              Number(timelineMatch.sideBRegistrationId || 0) > 0
                            }
                          />
                        </div>
                      </div>
                      {scoreSets.length > 0 && (
                        <div className="public-tournament-timeline-mobile-score">
                          <SideScoreDisplay sets={scoreSets} side="A" />
                          <span className="public-tournament-timeline-mobile-score-separator">
                            x
                          </span>
                          <SideScoreDisplay sets={scoreSets} side="B" />
                        </div>
                      )}
                    </div>
                    <time className="public-tournament-timeline-time">
                      <span>{t.timeline.scheduled}</span>
                      <strong>{formatTime(slot.slotStartAt)}</strong>
                    </time>
                    <span className="public-tournament-timeline-court">
                      {slotCourtLabel(slot.courtName)}
                    </span>
                    <div className="public-tournament-timeline-match">
                      <span className="public-tournament-timeline-phase">
                        {categoryLabel(categories, slot.categoryKey)}{t.scoreSeparator}
                        {timelinePhaseLabel(slot)}
                      </span>
                      <div className="public-tournament-timeline-sides">
                        <PublicTournamentMatchSide
                          avatarSexes={[
                            timelineMatch.sideAPlayerSex,
                            timelineMatch.sideAPartnerSex,
                          ]}
                          avatarUrls={[
                            timelineMatch.sideAPlayerAvatarUrl,
                            timelineMatch.sideAPartnerAvatarUrl,
                          ]}
                          label={timelineMatch.sideALabel || t.fallback.side}
                          profileHrefs={[
                            publicPlayerHref(
                              timelineMatch.sideAPlayerPublicPlayerId,
                            ),
                            publicPlayerHref(
                              timelineMatch.sideAPartnerPublicPlayerId,
                            ),
                          ]}
                          showAvatars={
                            Number(timelineMatch.sideARegistrationId || 0) > 0
                          }
                        />
                        <strong>x</strong>
                        <PublicTournamentMatchSide
                          avatarSexes={[
                            timelineMatch.sideBPlayerSex,
                            timelineMatch.sideBPartnerSex,
                          ]}
                          avatarUrls={[
                            timelineMatch.sideBPlayerAvatarUrl,
                            timelineMatch.sideBPartnerAvatarUrl,
                          ]}
                          label={timelineMatch.sideBLabel || t.fallback.side}
                          profileHrefs={[
                            publicPlayerHref(
                              timelineMatch.sideBPlayerPublicPlayerId,
                            ),
                            publicPlayerHref(
                              timelineMatch.sideBPartnerPublicPlayerId,
                            ),
                          ]}
                          showAvatars={
                            Number(timelineMatch.sideBRegistrationId || 0) > 0
                          }
                        />
                      </div>
                    </div>
                      <div className="public-tournament-timeline-result">
                        {scoreSets.length > 0 && (
                          <ScoreDisplay
                            sets={scoreSets}
                            className="is-timeline"
                          />
                        )}
                        <div className="public-tournament-status-stack">
                          <span className="public-tournament-status">
                            {matchStatusLabel(status)}
                          </span>
                          {elapsedLabel && (
                            <small className="public-tournament-elapsed">
                              {elapsedLabel}
                            </small>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </>
        )}
      </section>
      {sharePreview && (
        <div
          className="public-tournament-share-preview-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label={t.sharePreview.ariaLabel}
        >
          <div className="public-tournament-share-preview-modal">
          <div className="public-tournament-share-preview-head">
            <div>
              <strong>{t.sharePreview.title}</strong>
              <span>{t.sharePreview.description}</span>
            </div>
            <button
              type="button"
              className="public-tournament-share-preview-close"
              onClick={closeSharePreview}
              aria-label={t.sharePreview.closeAria}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                close
              </span>
            </button>
          </div>
          <div className="public-tournament-share-preview-frame">
            <img src={sharePreview.url} alt={t.sharePreview.imageAlt} />
          </div>
          <div className="public-tournament-share-preview-actions">
            <button
              type="button"
              className="public-tournament-share-button"
              onClick={onNativeSharePreview}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                ios_share
              </span>
              {t.actions.share}
            </button>
            <button
              type="button"
              className="public-tournament-share-button"
              onClick={onDownloadSharePreview}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                download
              </span>
              {t.actions.downloadPng}
            </button>
          </div>
          </div>
        </div>
      )}
    </>
  );
}
